import { inject, Injectable } from '@angular/core';
import { TradeKey } from '../model/api';
import { AxiosRequestConfig } from 'axios';
import { MarketId, Markets } from '../model/common';
import { BinanceRestClientOptions, BinanceUsdmRestClient, BybitRestClient, BybitRestClientOptions, OkxRestClient, OkxRestClientOptions } from '.';
import { BinanceUsdmTimeService, BybitTimeService, OkxTimeService } from './market-time.service';
import { MarketLoggerService } from './market-logger.service';

type AnyClient = BybitRestClient | BinanceUsdmRestClient | OkxRestClient

@Injectable({
  providedIn: 'root'
})
export class RestClientFactory {

  private marketLogger = inject(MarketLoggerService)
  private cache = new Map<string, AnyClient>()

  constructor() {
    BybitRestClient.timeService = inject(BybitTimeService)
    BinanceUsdmRestClient.timeService = inject(BinanceUsdmTimeService)
    OkxRestClient.timeService = inject(OkxTimeService)
  }

  createForKey(key: TradeKey) {
    switch (key.market) {
      case Markets.binance: return new RestClientWrapper(this.binanceUsdm(key))
      case Markets.bybit: return new RestClientWrapper(this.bybit(key))
      case Markets.okx: return new RestClientWrapper(this.okx(key))
    }
    throw new Error(`Unknown ${key.market}`)
  }

  createForMarket(market: MarketId) {
    switch (market) {
      case Markets.binance: return new RestClientWrapper(this.binanceUsdm())
      case Markets.bybit: return new RestClientWrapper(this.bybit())
      case Markets.okx: return new RestClientWrapper(this.okx())
    }
    throw new Error(`Unknown ${market}`)
  }

  bybit(key?: TradeKey) {
    const cacheKey = 'bybit' + (key ? key.id : '')
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)! as BybitRestClient
    }

    const options: BybitRestClientOptions = {
      recv_window: 5000,
    }
    const requestConfig: AxiosRequestConfig = {
      timeout: 10_000,
      headers: {
        'x-referer': 'cscalp',
      }
    }
    if (key) {
      options.key = key.authData.publicKey
      options.secret = key.authData.secretKey
    }
    const client = new BybitRestClient(options, requestConfig, this.marketLogger)

    this.cache.set(cacheKey, client)

    return client
  }

  binanceUsdm(key?: TradeKey) {
    const cacheKey = 'binanceUsdm' + (key ? key.id : '')
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)! as BinanceUsdmRestClient
    }

    const options: BinanceRestClientOptions = {
      recvWindow: 5000,
    }
    const requestConfig: AxiosRequestConfig = {
      timeout: 10_000,
    }
    if (key) {
      options.api_key = key.authData.publicKey
      options.api_secret = key.authData.secretKey
    }
    const client = new BinanceUsdmRestClient(options, requestConfig, this.marketLogger)

    this.cache.set(cacheKey, client)

    return client
  }

  okx(key?: TradeKey) {
    const cacheKey = 'okx' + (key ? key.id : '')
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)! as OkxRestClient
    }

    const options: OkxRestClientOptions = {}
    const requestConfig: AxiosRequestConfig = {
      timeout: 10_000,
    }
    if (key) {
      options.apiKey = key.authData.publicKey
      options.apiSecret = key.authData.secretKey
      options.apiPass = key.authData.password
    }
    const client = new OkxRestClient(options, requestConfig, this.marketLogger)

    this.cache.set(cacheKey, client)

    return client
  }

}


export class RestClientWrapper {

  constructor(readonly client: AnyClient) {}

  get isBybit() { return this.client instanceof BybitRestClient }
  get isBinanceUsdm() { return this.client instanceof BinanceUsdmRestClient }
  get isOkx() { return this.client instanceof OkxRestClient }

  get bybit(): BybitRestClient {
    if (this.client instanceof BybitRestClient) {
      return this.client
    }
    throw new Error('RestClient type error')
  }

  get binanceUsdm(): BinanceUsdmRestClient {
    if (this.client instanceof BinanceUsdmRestClient) {
      return this.client
    }
    throw new Error('RestClient type error')
  }

  get okx(): OkxRestClient {
    if (this.client instanceof OkxRestClient) {
      return this.client
    }
    throw new Error('RestClient type error')
  }

}
